import { useCallback, useEffect, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";

import { updateBoard } from "../../../../../../../services/apiCards";
import {
  useWorkflowById,
  useWorkflowStepNameByWorkflowStepId,
} from "../../../../../../../reactQuery/workflows";
import useMutate from "../../../../../../../hooks/useMutate";
import useBoardIdByKey from "../../../../../../../hooks/useBoardIdByKey";
import { useResponsive } from "../../../../../../../hooks/useResponsive";
import { getResponseData } from "../../../../../../../reactQuery/response";

export function useChangeBoardWorkflowState({
  close,
  currentWorkflowStepId,
  card,
  cardId,
  currentBoardWorkflowId,
  isTodo,
  targetBoardId,
  workflowId,
}) {
  const { id } = useBoardIdByKey();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const isMobile = useResponsive("down", "md");
  const cardData = useMemo(() => getResponseData(card, card ?? {}), [card]);
  const { data } = useWorkflowById(workflowId, targetBoardId);
  const workflowSteps = useMemo(
    () =>
      [...getResponseData(data, [])].sort(
        (left, right) => left.step_sequence - right.step_sequence
      ),
    [data]
  );
  const { workflowStepName } = useWorkflowStepNameByWorkflowStepId(
    currentWorkflowStepId,
    cardData?.board_id
  );
  const currentWorkflowStep = getResponseData(workflowStepName, {});

  const formMethods = useForm({
    defaultValues: {
      targetStep: "",
    },
  });
  const { handleSubmit, setValue, watch } = formMethods;
  const targetWorkflowStep = watch("targetStep");

  useEffect(() => {
    if (!workflowSteps.length) {
      return;
    }

    setValue("targetStep", workflowSteps[0]?.workflow_step_id ?? "");
  }, [setValue, workflowSteps]);

  const { mutate: updateMutate, isPending } = useMutate(
    updateBoard,
    ["boards", "watching", "mineCards"],
    () => {},
    () => {
      const nextSearchParams = new URLSearchParams(searchParams);
      nextSearchParams.set("brd_id", targetBoardId);
      setSearchParams(nextSearchParams);
      queryClient.invalidateQueries({
        queryKey: ["cards", id],
      });
      queryClient.invalidateQueries({
        queryKey: ["card", cardId],
      });
      queryClient.invalidateQueries({
        queryKey: ["history", String(cardId)],
      });
      close(false);
    },
    () => {}
  );

  const shouldSelectTargetStep = currentBoardWorkflowId !== workflowId && !isTodo;
  const isSubmitDisabled = shouldSelectTargetStep
    ? !targetWorkflowStep || isPending
    : isPending;

  const onSubmit = useCallback(
    ({ targetStep }) => {
      updateMutate({
        card_id: cardId,
        board_id: targetBoardId,
        ...(!isTodo
          ? { workflow_step_id: targetStep ?? currentWorkflowStepId }
          : {}),
      });
    },
    [cardId, currentWorkflowStepId, isTodo, targetBoardId, updateMutate]
  );

  return {
    currentWorkflowStepStatus: currentWorkflowStep?.status ?? "",
    formMethods,
    handleMove: handleSubmit(onSubmit),
    isMobile,
    isPending,
    isSubmitDisabled,
    shouldSelectTargetStep,
    workflowSteps,
  };
}
